import { Box, Stack } from '@mui/material';
import Decimal from 'decimal.js';
import { useCallback, useMemo } from 'react';
import { makeStyles } from 'tss-react/mui';

import { HeaderList } from '@/components/lists/HeaderList/HeaderList';
import { useDispositiveProvisionsContext } from '@/modules/dispositiveProvisions/contexts/dispositiveProvisions.context';
import { isBeneficiaryDesignationEntityType } from '@/modules/dispositiveProvisions/dispositiveProvisions.label';
import { SpecifyDispositiveProvisionsButton } from '@/modules/dispositiveProvisions/SpecifyDispositiveProvisionsButton';
import { getNodes } from '@/utils/graphqlUtils';

import { DeathOrderSelector } from '../DispositiveProvisionsFormV2/components/header/DeathOrderSelector';
import { DispositiveProvisions_DispositionScenarioFragment } from '../graphql/DispositiveProvisions.fragments.generated';
import { DispositiveProvisionDirection } from './DispositiveProvisionsByDeath.types';
import { sortDispositiveProvisions } from './DispositiveProvisionsListView.utils';
import { DispositiveProvisionRow } from './DispositiveProvisionsRow';
import { useAssertIsOnTwoClientHousehold } from './hooks/useAssertIsOnTwoClientHousehold';
import { ActiveDispositionTab } from './hooks/useDispositiveProvisionsListViewTab';

const useHeaderListClasses = makeStyles()(() => ({
  // eslint-disable-next-line tss-unused-classes/unused-classes
  root: {
    gap: 0,
  },
  // eslint-disable-next-line tss-unused-classes/unused-classes
  childContainer: {
    gap: 0,
  },
  // eslint-disable-next-line tss-unused-classes/unused-classes
  childWrapper: {
    padding: '0px 12px 0 0',
  },
}));

type DispositiveProvisionsForTwoClientHouseholdProps = {
  dispositionScenarioForTab: DispositiveProvisions_DispositionScenarioFragment | null;
  currentNodeValue?: Decimal;
} & ActiveDispositionTab;

export function DispositiveProvisionsForTwoClientHousehold({
  dispositionScenarioForTab,
  currentNodeValue,
  setActivePrimaryClientIdTab,
  activePrimaryClientIdTab,
  defaultTab,
}: DispositiveProvisionsForTwoClientHouseholdProps) {
  const { classes: headerListClasses } = useHeaderListClasses();
  // Guarantee we are on a two client household
  const { primaryClients } = useAssertIsOnTwoClientHousehold();
  const { entityType } = useDispositiveProvisionsContext();
  const isBenDesignation = isBeneficiaryDesignationEntityType(entityType);

  const handleDeathOrderChange = useCallback(
    (clientId: string) => {
      setActivePrimaryClientIdTab(clientId);
    },
    [setActivePrimaryClientIdTab]
  );

  const provisions = dispositionScenarioForTab?.dispositiveProvisions;
  const template = dispositionScenarioForTab?.dispositiveProvisionsTemplate;
  const secondDeathProvisions =
    dispositionScenarioForTab?.secondDeathDispositiveProvisions;
  const secondDeathTemplate =
    dispositionScenarioForTab?.secondDeathDispositiveProvisionsTemplate;

  const firstDeathDispositiveProvisions = useMemo(() => {
    if (template) {
      return getNodes(template.dispositiveProvisions).sort(
        sortDispositiveProvisions
      );
    }
    return getNodes(provisions).sort(sortDispositiveProvisions);
  }, [provisions, template]);

  const secondDeathDispositiveProvisions = useMemo(() => {
    if (secondDeathTemplate) {
      return getNodes(secondDeathTemplate.dispositiveProvisions).sort(
        sortDispositiveProvisions
      );
    }
    return getNodes(secondDeathProvisions).sort(sortDispositiveProvisions);
  }, [secondDeathProvisions, secondDeathTemplate]);

  const normalizedDispositionScenariosFirstDeath = useMemo(
    () => ({
      heading: 'Upon first death',
      noItemsText: isBenDesignation
        ? 'No beneficiary designations specified'
        : 'No dispositions specified upon first death',
      items: firstDeathDispositiveProvisions,
    }),
    [firstDeathDispositiveProvisions, isBenDesignation]
  );

  const secondPrimaryClientDeathId = useMemo(() => {
    if (activePrimaryClientIdTab === primaryClients[0].id) {
      return primaryClients[1].id;
    } else {
      return primaryClients[0].id;
    }
  }, [primaryClients, activePrimaryClientIdTab]);

  const normalizedDispositionScenariosSecondDeath = useMemo(
    () => ({
      heading: 'Upon second death',
      noItemsText: isBenDesignation
        ? 'No beneficiary designations specified'
        : 'No dispositions specified upon second death',
      items: secondDeathDispositiveProvisions,
    }),
    [secondDeathDispositiveProvisions, isBenDesignation]
  );

  return (
    <Stack spacing={2}>
      <Box>
        <Stack direction="row" justifyContent="flex-end" mb={2}>
          <DeathOrderSelector
            selectedFirstDeathClientId={activePrimaryClientIdTab}
            onDeathOrderChange={handleDeathOrderChange}
          />
        </Stack>
        <HeaderList
          heading={normalizedDispositionScenariosFirstDeath.heading}
          noItemsText={normalizedDispositionScenariosFirstDeath.noItemsText}
          data-testid="upon-first-death-dispositive-provisions"
          classes={headerListClasses}
        >
          {normalizedDispositionScenariosFirstDeath.items.map((item) => (
            <DispositiveProvisionRow
              primaryClients={primaryClients}
              key={item.id}
              provision={item}
              dyingPrimaryClientId={activePrimaryClientIdTab}
              calculatedProvisionAmount={null}
              direction={DispositiveProvisionDirection.Distributing}
            />
          ))}
        </HeaderList>
        <HeaderList
          heading={normalizedDispositionScenariosSecondDeath.heading}
          noItemsText={normalizedDispositionScenariosSecondDeath.noItemsText}
          data-testid="upon-second-death-dispositive-provisions"
          classes={headerListClasses}
        >
          {normalizedDispositionScenariosSecondDeath.items.map((item) => (
            <DispositiveProvisionRow
              primaryClients={primaryClients}
              key={item.id}
              provision={item}
              dyingPrimaryClientId={secondPrimaryClientDeathId}
              calculatedProvisionAmount={null}
              direction={DispositiveProvisionDirection.Distributing}
            />
          ))}
        </HeaderList>
      </Box>
      <SpecifyDispositiveProvisionsButton
        buttonProps={{
          fullWidth: true,
          sx: {
            px: 2,
          },
        }}
        behavior="edit"
        currentNodeValue={currentNodeValue}
        setActivePrimaryClientIdTab={setActivePrimaryClientIdTab}
        activePrimaryClientIdTab={activePrimaryClientIdTab}
        defaultTab={defaultTab}
      />
    </Stack>
  );
}
